import React from "react";
import { NavLink } from "react-router-dom";
import { useT } from "@/context/I18nContext";
import { cn } from "@/lib/utils";
import LanguageToggle from "./LanguageToggle";
import BrandLogo from "@/components/common/BrandLogo";
import { isPlatformOwner, isSuperAdmin, isOrgAdmin, isFlmAdmin, isCareProfessional, isOccupationalHealth } from "@/context/AuthContext";
import {
  Home, Sparkles, ClipboardCheck, ListTodo, LineChart, Waves, Trophy, HeartPulse, Settings,
  Shield, Users, MessageSquare, Flag, MailPlus, KeyRound, Activity, MessagesSquare,
  Building2, Users2, MapPin, LayoutGrid, PieChart, ScrollText, HeartHandshake, Bell, FolderOpen, FileText, Star, BookHeart,
  Stethoscope, Briefcase, Lock,
} from "lucide-react";

const iconMap = {
  home: Home, companion: Sparkles, checkins: ClipboardCheck, plan: ListTodo, insights: LineChart,
  rhythm: Waves, victories: Trophy, memories: BookHeart, safety: HeartPulse, settings: Settings,
  overview: Home, users: Users, conversations: MessageSquare, flagged: Flag, invites: MailPlus, beta: KeyRound,
  usage: Activity, feedback: MessagesSquare, organisation: Building2, people: Users2, teams: Users2,
  locations: MapPin, departments: LayoutGrid, analytics: PieChart, wellbeing: HeartHandshake, audit: ScrollText,
  clients: Users2, alerts: Bell, dossier: FolderOpen, reports: FileText, admin: Shield, star: Star,
  owner: Shield, business: Briefcase, oh: Stethoscope, consent: Lock, care: HeartHandshake,
};

function NavItem({ to, k, keyName, end }) {
  const t = useT();
  const Icon = iconMap[keyName] || Home;
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`nav-${keyName}`}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition",
          isActive
            ? "bg-white text-ink-900 shadow-card"
            : "text-ink-500 hover:text-ink-900 hover:bg-white/60"
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
      <span className="truncate">{t(k)}</span>
    </NavLink>
  );
}

function Section({ label, children }) {
  return (
    <div className="mb-6">
      {label && (
        <div className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">
          {label}
        </div>
      )}
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

export default function Sidebar({ roles, mode, mobile = false }) {
  const t = useT();
  const owner = isPlatformOwner(roles) || isSuperAdmin(roles);
  const orgAdmin = isOrgAdmin(roles) || isFlmAdmin(roles);
  const care = isCareProfessional(roles);
  const oh = isOccupationalHealth(roles);
  const hasProfessional = owner || orgAdmin || care || oh;

  return (
    <aside
      className={mobile
        ? "flex flex-col w-full h-full px-4 py-6"
        : "hidden lg:flex flex-col w-64 shrink-0 border-r border-border bg-cream-50/80 backdrop-blur px-4 py-6"}
      data-testid={mobile ? "mobile-sidebar" : "app-sidebar"}
    >
      <div className="flex items-center gap-2 px-2 mb-8">
        <BrandLogo size="lg" />
        <div className="leading-tight">
          <div className="font-heading font-semibold text-ink-900 tracking-tight">
            {t("brand.name")}
          </div>
          <div className="text-[11px] text-ink-500">{t("brand.subtitle")}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto -mr-2 pr-2">
        {/* Professional entry points — navigation changes with role; nothing is shown that the role cannot use. */}
        {hasProfessional && (
          <Section label={t("b2b.nav.professional")}>
            {owner && <NavItem to="/owner" k="b2b.nav.owner" keyName="owner" />}
            {orgAdmin && mode !== "care" && <NavItem to="/admin" k="nav.business" keyName="business" />}
            {care && mode !== "business" && <NavItem to="/app/care" k="nav.care" keyName="care" />}
            {oh && <NavItem to="/oh" k="b2b.nav.oh" keyName="oh" />}
          </Section>
        )}

        {care && mode !== "business" && (
          <Section label={t("nav.care")}>
            <NavItem to="/app/care" end k="nav.overview" keyName="overview" />
            <NavItem to="/app/care/clients" k="nav.clients" keyName="clients" />
            <NavItem to="/app/care/alerts" k="nav.alerts" keyName="alerts" />
            <NavItem to="/app/care/dossier" k="nav.dossier" keyName="dossier" />
          </Section>
        )}

        <Section label={hasProfessional ? t("b2b.nav.personal") : undefined}>
          <NavItem to="/app" end k="nav.home" keyName="home" />
          <NavItem to="/app/companion" k="nav.companion" keyName="companion" />
          <NavItem to="/app/checkins" k="nav.checkins" keyName="checkins" />
          <NavItem to="/app/plan" k="nav.plan" keyName="plan" />
          <NavItem to="/app/craving" k="client.craving" keyName="star" />
          <NavItem to="/app/insights" k="nav.insights" keyName="insights" />
          <NavItem to="/app/rhythm" k="nav.rhythm" keyName="rhythm" />
          <NavItem to="/app/victories" k="nav.victories" keyName="victories" />
          <NavItem to="/app/memories" k="nav.memories" keyName="memories" />
          <NavItem to="/app/reflection" k="client.reflection" keyName="settings" />
          <NavItem to="/app/quiet-returns" k="client.quietReturn" keyName="memories" />
          <NavItem to="/app/safety" k="nav.safety" keyName="safety" />
          <NavItem to="/app/consent" k="b2b.nav.consent" keyName="consent" />
        </Section>
      </nav>

      <div className="pt-4 border-t border-border flex items-center justify-between gap-2">
        <NavItem to="/app/settings" k="common.settings" keyName="settings" />
        <LanguageToggle />
      </div>
    </aside>
  );
}
